import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useHeader } from '../../context/HeaderContext';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { useAnalytics } from '../../hooks/useAnalytics';
import type { SupportedLanguages } from '../../i18n';
import ChatWidget from './ChatWidget';

const ChatbotSection: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const section = useSection();
  const currentLanguage = i18n.language as SupportedLanguages;

  const { trackSectionView } = useAnalytics({
    sectionId: section?.id,
    sectionName: section?.names?.fr,
    sectionType: 'chatbot-ia'
  });

  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(currentLanguage);

  useEffect(() => {
    if (section?.names) {
      const name = section.names[currentLanguage] || section.names.fr;
      setTitle(name);
    }
  }, [section, currentLanguage, setTitle]);

  useEffect(() => {
    if (section?.id) {
      trackSectionView();
    }
  }, [section?.id]);

  if (configLoading || translationsLoading) {
    return <LoadingIndicator config={config} translations={translations} />;
  }

  if (!config) {
    return <LoadingIndicator />;
  }

  const sectionBgColor = resolveColor(config['sectionBack-color'] || config['light-color'], config, '#F9FAFB');
  const sectionTitle = section?.names ? (section.names[currentLanguage] || section.names.fr) : 'Assistant IA';

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: sectionBgColor,
        fontFamily: config.font_family || 'system-ui'
      }}
    >
      <SectionHeader
        config={config}
        title={sectionTitle}
        onBackClick={() => navigate('/')}
        translations={translations || {}}
      />

      <main
        className="fixed inset-0 pb-[40px]"
        style={{ zIndex: 1, paddingTop: 'var(--header-height)' }}
      >
        <ChatWidget
          hotelId={config.id}
          hotelName={config.name}
          language={currentLanguage}
          majorColor={config['major-color']}
          receptionPhone={config.telephone}
          allowReceptionChat={section?.allow_reception_chat === true}
        />
      </main>
    </div>
  );
};

export default ChatbotSection;
